import { Roles } from './common.constants';

export interface Session {
  expiredIn: number;
}

export interface UserModel {
  id: string;
  userRef: string;
  clinicRef: string;
  email: string;
  role: Roles;
}

export interface Clinic {
  id: string;
  role: string;
  status: string;
  createdAt: number;
  updatedAt: number;
  email: string;
  name: string;
  websiteUrl: string;
  logoId: string;
  widgetCode: string;
}

export interface ClinicPublicInfo {
  id: string;
  is_active: boolean;
  name: string;
  redirect_url: string;
  logoUrl: string;
  simulationsAmount: SimulationsAmount;
  email: string;
  website_url: string;
  legalterms_url: string;
  chat_enabled: boolean;
  phone_required?: boolean;
  auto_email_patient?: boolean;
  sim_v2_visible?: boolean;
}

export interface ClinicFullBase {
  id: string;
  // ref_code: string;
  // active_until: string;
  clinicUsers: ClinicUser[];
  name: string;
  email: string;
  website_url: string;
  logo: string;
  payment_plan: string;
  phone_required?: boolean;
  auto_email_patient?: boolean;
  sim_v2_visible?: boolean;
}

export interface ClinicFullRaw extends ClinicFullBase {
  patients: any[];
  widget_code: string;
  // clinicUsers: ClinicUser[];
}

export interface ClinicFull extends ClinicFullBase {
  // clinicUsers: ClinicUser[];
  clinicOwner: ClinicUser[];
  cliniAdmins: ClinicUser[];
}

export interface ClinicUser {
  user: string;
  role: 'Clinic User' | 'Clinic Admin' | 'Clinic Owner';
}

export interface ClinicBrief {
  id: string;
  ref_code: string;
  active_until: string;
  auto_email_patient: boolean;
  email: string;
  leads_email: string;
  legalterms_url: string;
  logo: string;
  name: string;
  phone_required: boolean;
  sim_v2_visible: boolean;
  simulationsAmount: SimulationsAmount;
  website_url: string;
}

export interface SimulationsAmount {
  total: number;
  thisMonth: number;
  available: number;
}

export class User {
  constructor(
    public email: string,
    public id: string,
    public userRef: string,
    public clinicRef: string,
    public role: Roles,
    private _tokenAccess: string,
    private _tokenRefresh: string,
  ) {
  }

  get token() {
    return this._tokenAccess;
  }

  get tokenRefresh() {
    return this._tokenRefresh;
  }
}

export interface AuthResponse {
  access: string;
  refresh: string;
}

export type Direction = 'horizontal' | 'vertical';

export interface Control {
  id: string;
  title?: string;
  icon?: string;
  items?: Control[];
}

export interface ColorControl extends Control {
  diffuseColor: number[];
  ambientColor: number[];
  colorDir: number[];
}

export interface ArDataResponse {
  COLORS: Control[];
  TYPES: Control[];
  STYLES: Control[];
  LENGTH: Control[];
}

export interface PatientSimulation {
  id: string;
  index: number;
  imgPreview: string;
  imgUrl: string;
  imgId: string;
  createdAt: string;
  isClinicCreated: boolean;
  imagesMl: string[];
  imagesMLv2: string[][];
  status: ML_STATUS;
  arSettings: ImageProperties;
  isLeadCapturing?: boolean;
}

export enum ML_STATUS {
  RECEIVED = 'RECEIVED',
  RETRY = 'RETRY',
  PENDING = 'PENDING',
  STARTED = 'STARTED',
  FAILURE = 'FAILURE',
  SUCCESS = 'SUCCESS',
}

export interface ImageProperties {
  image?: string;
  model: ModelProperties;
  color: ColorProperties;
  position: PositionProperties;
}

export interface ToneObject {
  ambientColor: number[];
  colorDir: number[];
  diffuseColor: number[];
}

export type SexId = 'ml' | 'fml';
export type EthnicityId = 'wt' | 'bl' | 'az';
export type HairlossId = 'hl_1' | 'hl_2' | 'hl_3';
export type TypeId = 'st' | 'cr';
export type StyleId = 'ur' | 'ul' | 'sr' | 'sl';
export type LengthId = 's' | 'm' | 'l';
// export type ColorId = 'black' | 'grey' | 'brown' | 'blonde' | 'red';
export type ColorId = string;

export interface ModelProperties {
  sex: SexId;
  ethnicity: EthnicityId;
  hairloss: HairlossId;
  type: TypeId;
  style: StyleId;
  length: LengthId;
  volume: {
    r1: number;
    r2: number;
    r3: number;
  };
}

export interface ColorProperties {
  color: ColorId;
  // tone: number;  // 1-100% real null (take 12345)
  brightness: number;  // 0-2 // real 2,3,4
  lightDirection: number;  // -10 - 10  real 12345
}

export interface PositionProperties {
  volume?: number; // 0-12 used locally only!!!
  width: number; // 0.8 - 1.2
  height: number;  // -0.5 - 0.5
  depth: number;  // -0.5 - 0.5
  hairline: number;  // -100 - 100
}

export interface PatientData {
  id: string;
  refCode: string;
  clinic: string;
  clinicRefCode: string;
  name: string;
  simulations: PatientSimulation[];
  ai_simulations: any;
  default_image_url: string;
}

export interface PostImageParams {
  clinic?: string;
  device_id?: string;
  patient?: string;
  image: string;
  parent?: string;
  customValues?: ImageProperties;
  origin: UploadedImageOrigin;
}

export enum UploadedImageOrigin {
  FE = 'FE',
  FEHDI = 'FEHDI',
  FELDI = 'FELDI',
}

export interface CreateV2SimulationTrigger {
  image: string;
  image_low_density: string;
  image_high_density: string;
}

export interface ImageResp {
  id: string;
  image: string;
  device_id: string;
  creator: string;
  parent: string;
  clinic: string;
  is_valid?: boolean;
}

export interface PostNewPatient {
  email: string;
  name: string;
  phone: string;
  parent_image_id: string;
  arSettings: ImageProperties;
  notes?: string;
}

export interface PostNewPatientResp {
  id: string;
  refCode: string;
  email: string;
  name: string;
  phone: string;
  notes: string;
  device_id: string;
  clinic: string;
  clinicRefCode: string;
  parent_image_id: string;
  default_image_url: string;
}

export type DrawImgOptions = [
  CanvasImageSource,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
];

export interface Patient {
  id: string;
  refCode: string;
  name: string;
  phone: string;
  email: string;
  default_image_url: string;
  clinic: string;
  clinicRefCode: string;
  device_id: string;
  hasClinicSims: boolean;
  createdAt: string;
  simulations: PatientSimulation[];
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  monthly_price: number;
}

export interface Coupon {
  id: string;
  description: string;
  code: string;
  discount_pct: number;
  months: number;
}

export interface BasePayload {
  count: number;
}

export interface ImagesPayload extends BasePayload {
  results: ImageFull[];
}

export interface ClinicPayload extends BasePayload {
  results: ClinicBrief[];
}

export interface ClinicFullPayload extends BasePayload {
  results: ClinicFullRaw[];
}

export interface PatientPayload extends BasePayload {
  results: Patient[];
}


export interface PlansPayload extends BasePayload {
  results: Plan[];
}


export interface CouponsPayload extends BasePayload {
  results: Coupon[];
}

export interface ImageFull {
  clinic: string;
  clinicRefCode: string;
  creator: string;
  device_id: string;
  id: string;
  image: string;
  is_valid: boolean;
  parent: string;
  patient: string;
  patientRefCode: string;
  createdAt: string;
  clinicName: string;
  patientName: string;
  patientType: string;
}
